import React, { useState } from 'react';
import { FiPlus, FiMinus, FiHelpCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
{
  q: "What is the teacher-to-student ratio?",
  a: "We maintain a balanced teacher-to-student ratio to ensure personalized attention, effective learning, and strong academic support for every student."
},
    {
      q: "What are your school timings?",
      a: "Our regular school hours are from 08:30 AM to 03:00 PM, Monday to Friday. We are closed on Saturdays and Government Holidays."
    },
    {
      q: "How do you ensure child safety?",
      a: "Your child's safety is our highest priority. We have CCTV monitored premises, child-safe infrastructure, verified staff, and a secure check-in/check-out system."
    },
    {
      q: "Do you provide transport facilities?",
      a: "Yes, we provide safe and comfortable transport facilities within a 5-7km radius of our Jubilee Hills campus."
    },
    {
      q: "What age groups do you enroll?",
      a: "We currently enroll children from 2 years (Toddlers) to 5 years (Pre-K). We follow a strictly age-appropriate program for each group."
    },
    {
      q: "What is your admission process?",
      a: "The process starts with a campus visit and interaction with our center head. This is followed by a simple registration process. We encourage parents to schedule a tour first."
    },
    {
      q: "What curriculum do you follow?",
      a: "We follow a comprehensive play-based curriculum that combines Montessori principles with modern early childhood education standards. Our program focuses on holistic development including cognitive, social, emotional, and physical growth."
    },
    {
      q: "Are meals provided?",
      a: "Yes, we provide nutritious, home-cooked meals including breakfast, lunch, and evening snacks. Our menu is designed by nutritionists and accommodates various dietary requirements. Parents can also provide their child's meals if preferred."
    },
    {
      q: "What activities do children participate in?",
      a: "Our daily activities include art, music, dance, outdoor play, story time, sensory activities, and age-appropriate learning games. We also organize special events, field trips, and seasonal celebrations throughout the year."
    },
    {
      q: "What are the teacher qualifications?",
      a: "All our teachers hold relevant early childhood education qualifications and undergo regular training. Our lead teachers have Montessori certification and extensive experience in child development. We also conduct background verification for all staff."
    },
    {
      q: "How do you handle discipline?",
      a: "We believe in positive reinforcement and gentle guidance rather than punishment. Our approach focuses on teaching self-regulation, empathy, and appropriate social behavior through modeling and positive encouragement."
    },
    {
      q: "What is the fee structure?",
      a: "Our fees are competitive and include tuition, meals, activities, and basic supplies. We offer quarterly payment plans and sibling discounts. Detailed fee breakdown is provided during the admission process."
    },
    {
      q: "How do you communicate with parents?",
      a: "We maintain regular communication through daily reports, monthly newsletters, parent-teacher meetings, and our mobile app. Parents receive updates on their child's progress, activities, and any important announcements."
    },
    {
      q: "Do you accommodate children with special needs?",
      a: "We strive to be inclusive and work with children who have mild to moderate special needs. Our teachers are trained to adapt activities and provide additional support. For severe special needs, we recommend consulting with our center head."
    },
    {
      q: "What happens in case of emergency?",
      a: "We have established emergency procedures and maintain a first-aid kit. In case of medical emergencies, we contact parents immediately and take the child to the nearest hospital if required. All medical expenses are the responsibility of parents."
    },
    {
      q: "How do you handle sickness or illness?",
      a: "Children showing symptoms of illness should not be sent to school. If a child becomes unwell during school hours, parents are contacted immediately. We have isolation areas for sick children and follow strict hygiene protocols."
    },
    {
      q: "What supplies should parents provide?",
      a: "Parents need to provide labeled water bottles, extra clothes, diapers (for toddlers), and personal hygiene items. We provide all learning materials, art supplies, and classroom essentials."
    },
    {
      q: "How do you assess child development?",
      a: "We use continuous observation and age-appropriate assessments to track each child's progress. Parents receive regular progress reports and we conduct parent-teacher conferences to discuss development milestones."
    }
  ];

  return (
    <div className="animate-fadeIn">
      <SEO page="faq" />
      {/* Hero Section with Background */}
      <section className="relative bg-gradient-to-br from-blue-900 via-indigo-800 to-violet-900 pt-32 pb-40 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border-4 border-white rounded-full"></div>
          <div className="absolute top-20 right-20 w-20 h-20 border-4 border-white"></div>
          <div className="absolute bottom-10 left-1/4 w-24 h-24 border-4 border-white rounded-full"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center justify-center space-x-2 text-sm text-blue-200 mb-6" data-aos="fade-down">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <span className="text-blue-400">/</span>
            <span className="text-white font-medium">FAQ</span>
          </div>

          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6" data-aos="fade-up">
              <SafeIcon icon={FiHelpCircle} className="w-5 h-5 text-white" />
              <span className="text-white font-medium">Help Center</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" data-aos="fade-up" data-aos-delay="100">
              Frequently Asked Questions
            </h1>
            
            <p className="text-xl text-blue-100 leading-relaxed" data-aos="fade-up" data-aos-delay="200">
              Find answers to the most frequently asked questions about our programs, curriculum, safety measures, 
              and everything you need to know about your child's early learning journey.
            </p>
          </div>
        </div>

        {/* Decorative Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="w-full h-auto">
            <path fill="#ffffff" fillOpacity="1" d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,58.7C960,64,1056,64,1152,58.7C1248,53,1344,43,1392,37.3L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div 
                key={idx} 
                className="border border-emerald-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === idx ? -1 : idx)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-emerald-50/30 transition-colors"
                >
                  <span className="text-lg font-bold text-gray-800">{faq.q}</span>
                  <SafeIcon icon={openIndex === idx ? FiMinus : FiPlus} className="w-5 h-5 text-emerald-600" />
                </button>
                <div className={`transition-all duration-300 ${openIndex === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                  <div className="p-6 pt-0 text-gray-600 border-t border-emerald-50">
                    {faq.a}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;